import { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { PageHeader, Card, Btn, Table, Badge, InfoAlert, Pagination, FilterBar, FilterSelect } from '../components/UI';
import { fmt } from '../utils/helpers';

/**
 * Lodge outages.
 *
 * A lodge with every AP offline is treated as out of service. When it returns,
 * subscriptions that were live during the window are extended by the outage
 * duration so nobody pays for time they could not use.
 */
const STATUS_COLOR = { ongoing: 'red', resolved: 'yellow', compensated: 'green', ignored: 'gray' };

function humanMins(m) {
  if (m == null) return '—';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60), rem = m % 60;
  return rem ? `${h}h ${rem}m` : `${h}h`;
}

export default function Outages() {
  const [rows, setRows]       = useState([]);
  const [ongoing, setOngoing] = useState([]);
  const [settings, setSettings] = useState(null);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [status, setStatus]   = useState('all');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId]   = useState(null);
  const toast = useToast();

  const load = useCallback(async (p = 1, st = status) => {
    setLoading(true);
    try {
      const d = await api.getOutages({ page: p, limit: 20, ...(st !== 'all' ? { status: st } : {}) });
      setRows(d.outages || []);
      setOngoing(d.ongoing || []);
      setSettings(d.settings || null);
      setTotal(d.total || 0);
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [status]); // eslint-disable-line

  useEffect(() => { load(1); /* eslint-disable-next-line */ }, []);

  async function compensate(id) {
    setBusyId(id);
    try {
      const r = await api.compensateOutage(id);
      toast(r.message, 'success');
      load(page);
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setBusyId(null);
    }
  }

  async function ignore(id) {
    if (!window.confirm('Dismiss this outage without extending anyone?')) return;
    setBusyId(id);
    try {
      await api.ignoreOutage(id);
      toast('Outage dismissed.', 'success');
      load(page);
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setBusyId(null);
    }
  }

  const columns = [
    {
      key: 'lodge', label: 'Lodge',
      render: (o) => (
        <div>
          <div style={{ fontWeight: 600, color: 'var(--text)' }}>{o.lodge?.name || '—'}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{o.aps_down}/{o.aps_total} APs down</div>
        </div>
      ),
    },
    {
      key: 'window', label: 'Window',
      render: (o) => (
        <div style={{ fontSize: 12 }}>
          <div style={{ color: 'var(--text)' }}>{fmt.datetime(o.started_at)}</div>
          <div style={{ color: 'var(--text3)' }}>
            {o.ended_at ? `to ${fmt.datetime(o.ended_at)}` : 'still down'}
          </div>
        </div>
      ),
    },
    {
      key: 'duration', label: 'Duration',
      render: (o) => (
        <span style={{ fontWeight: 600, color: o.status === 'ongoing' ? 'var(--red)' : 'var(--text)' }}>
          {o.status === 'ongoing'
            ? humanMins(Math.round((Date.now() - new Date(o.started_at)) / 60000))
            : humanMins(o.duration_mins)}
        </span>
      ),
    },
    {
      key: 'status', label: 'Status',
      render: (o) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
          <Badge color={STATUS_COLOR[o.status] || 'gray'}>{o.status}</Badge>
          {o.site_wide ? <Badge color="yellow">site-wide</Badge> : null}
        </div>
      ),
    },
    {
      key: 'compensated', label: 'Extended',
      render: (o) => o.compensated_subs
        ? <span style={{ fontSize: 12, color: 'var(--green)' }}>
            {o.compensated_subs} sub{o.compensated_subs > 1 ? 's' : ''} · +{humanMins(o.compensated_mins)}
          </span>
        : <span style={{ fontSize: 12, color: 'var(--text3)' }}>—</span>,
    },
    {
      key: 'actions', label: '',
      render: (o) => (
        <div style={{ display: 'flex', gap: 6 }}>
          {o.status === 'resolved' && (
            <Btn size="xs" loading={busyId === o.id} onClick={() => compensate(o.id)}>Extend users</Btn>
          )}
          {o.status === 'compensated' && (
            <Btn size="xs" variant="ghost" loading={busyId === o.id} onClick={() => compensate(o.id)}>Re-run</Btn>
          )}
          {['resolved', 'ongoing'].includes(o.status) && !o.compensated_subs && (
            <Btn size="xs" variant="ghost" loading={busyId === o.id} onClick={() => ignore(o.id)}>Dismiss</Btn>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Outages"
        subtitle="Lodges that lost service, and the subscription time given back."
        action={<Btn variant="ghost" onClick={() => load(page)}>Refresh</Btn>}
      />

      {ongoing.length > 0 && (
        <div style={{
          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.35)',
          borderRadius: 10, padding: 14, marginBottom: 16,
        }}>
          <div style={{ fontWeight: 700, color: 'var(--red)', marginBottom: 6 }}>
            {ongoing.length} lodge{ongoing.length > 1 ? 's are' : ' is'} offline right now
          </div>
          {ongoing.map(o => (
            <div key={o.id} style={{ fontSize: 13, color: 'var(--text2)' }}>
              <strong style={{ color: 'var(--text)' }}>{o.lodge?.name}</strong> — down since{' '}
              {fmt.datetime(o.started_at)} ({humanMins(Math.round((Date.now() - new Date(o.started_at)) / 60000))})
            </div>
          ))}
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 8 }}>
            Affected subscriptions are extended automatically once service returns.
          </div>
        </div>
      )}

      {settings && (
        <InfoAlert>
          Outages under <strong>{settings.min_minutes} minutes</strong> are recorded but not compensated.
          Any single outage can add at most <strong>{humanMins(settings.max_extend_minutes)}</strong>.
          Compensation is currently <strong>{settings.auto_compensate ? 'automatic' : 'manual'}</strong>.
          A subscription can only ever be extended once per outage.
        </InfoAlert>
      )}

      <FilterBar>
        <FilterSelect
          value={status}
          onChange={(v) => { setStatus(v); setPage(1); load(1, v); }}
          options={[
            { value: 'all',         label: 'All outages' },
            { value: 'ongoing',     label: 'Ongoing' },
            { value: 'resolved',    label: 'Resolved — not yet extended' },
            { value: 'compensated', label: 'Compensated' },
            { value: 'ignored',     label: 'Dismissed' },
          ]}
        />
      </FilterBar>

      <Card style={{ padding: 0, marginTop: 16 }}>
        <Table columns={columns} rows={rows} loading={loading} emptyText="No outages recorded." />
      </Card>

      <Pagination page={page} total={total} limit={20} onChange={(p) => { setPage(p); load(p); }} />
    </div>
  );
}
